import { useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { zoneCaption } from "@/lib/appointmentZones";
import {
  formatDateInTz,
  formatTimeInTz,
} from "@/design-system/lib/format";
import { useUserTimeZone } from "@/utils/datetime";
import type { AppointmentDto } from "@/api/appointments";
import type { WorkTaskRelatedEntityType } from "@/api/workTasks";
import { appointmentDayZone } from "../lib/tz";
import { useAppointmentsByRelated } from "../hooks/useAppointments";
import { AppointmentFormModal } from "./AppointmentFormModal";

interface Props {
  relatedEntityType: WorkTaskRelatedEntityType;
  relatedEntityId: string;
  /**
   * The record's display name. Shown in the empty state and seeded as the
   * locked linked-record label when scheduling from here.
   */
  entityLabel: string;
  /**
   * An archived / put-away record keeps its history but loses the Schedule
   * button — mirrors how the detail pages already treat `isArchived` (every
   * tab renders; only write affordances are gated). New commitments against a
   * record someone deliberately shelved are the one thing that makes no sense.
   */
  entityArchived?: boolean;
}

/**
 * Total rows rendered on first paint, spent on upcoming first.
 *
 * Deliberately NOT TaskListMini's MAX_ROWS = 200, and deliberately not
 * imported from it: 50 is the number chosen for this panel. An appointment row
 * is taller than a task row and the history is unbounded, so a smaller first
 * paint with an explicit "show more" beats a 200-row wall. The divergence is
 * intentional — do not "fix" it by aligning the two constants.
 */
const INITIAL_ROWS = 50;

/** Rows appended per "Show 50 more" click. */
const PAGE_SIZE = 50;

/**
 * One row per series-or-standalone, keeping the order of the array it is given.
 *
 * 🔴 This is a RENDER-side collapse. The wire contract is unpaged BY DESIGN
 * (`appointmentsApi.listByRelated` takes no window and no `take`), so this must
 * not become a fetch narrowing — every occurrence still arrives, and the "Show
 * 50 more" control still pages from cache without a second request. Collapsing
 * server-side would also make the count in the header a different number from
 * the number of things the panel can show.
 *
 * The FIRST occurrence of a group wins, which is why this runs on each section
 * AFTER that section is ordered: `upcoming` is ascending, so the winner is the
 * next meeting; `past` is most-recent-first, so the winner is the last one that
 * happened. Both are the occurrence a reader of that section wants.
 *
 * A series with history AND future occurrences legitimately contributes a row
 * to BOTH sections — it is one series, but it is two answers to two different
 * questions, and the Past section would otherwise silently lose it.
 */
function collapseSeries(rows: AppointmentDto[]): AppointmentDto[] {
  const seen = new Set<string>();
  const out: AppointmentDto[] = [];
  for (const a of rows) {
    // A one-off carries no seriesId, so it is its own group.
    const key = a.seriesId ?? a.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/**
 * Linked "Calendar" panel for an entity's detail page — the appointment
 * back-reference, and the place to schedule one without leaving the record.
 * Structurally a clone of TaskListMini (header + count pill + primary button,
 * skeleton, role="alert" error with Retry, empty state, <ul> of rows); there is
 * no shared list-with-empty-state component to reuse.
 *
 * NO permission gate, deliberately. The Calendar module is authenticated-only
 * by design (SidebarMenu.cs:36-38 "open to every authenticated user"; the
 * /calendar route carries no permission), and all five Appointments.*
 * permissions are absent from RolePermissionMap — they are ADMIN-only via
 * Permissions.All and only ViewAll is ever checked. Mirroring TaskListMini's
 * hasPermission("Tasks.Create") gate as hasPermission("Appointments.Create")
 * would therefore disable this button for every non-admin in the system while
 * the identical action on /calendar kept working for them.
 *
 * The list is not own-scope — see appointmentsApi.listByRelated. Rows the
 * viewer neither owns nor attends arrive redacted (`isRedacted`), so they
 * render as time + Busy + owner name and are inert: there is nothing more to
 * open, and a modal full of blanked fields would be a worse answer than no
 * modal.
 */
export function AppointmentListMini({
  relatedEntityType,
  relatedEntityId,
  entityLabel,
  entityArchived = false,
}: Props) {
  const tz = useUserTimeZone();
  const q = useAppointmentsByRelated(relatedEntityType, relatedEntityId);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState<AppointmentDto | null>(null);
  // null = follow the auto rule (expanded only when nothing is upcoming);
  // once the user touches the toggle their choice wins.
  const [showPastOverride, setShowPastOverride] = useState<boolean | null>(null);
  const [extraPast, setExtraPast] = useState(0);

  const rows = q.data ?? [];

  // The server orders by start ascending. An appointment counts as upcoming
  // until it has ENDED, so a meeting in progress stays actionable rather than
  // dropping into history mid-call.
  //
  // Both sections are collapsed to one row per series AFTER the split, so a
  // weekly standing call is one line rather than fifty-two — see collapseSeries.
  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const up: AppointmentDto[] = [];
    const back: AppointmentDto[] = [];
    for (const a of rows) {
      if (Date.parse(a.endAt) >= now) up.push(a);
      else back.push(a);
    }
    // Past reads most-recent-first; upcoming keeps the server's ascending order.
    back.reverse();
    return { upcoming: collapseSeries(up), past: collapseSeries(back) };
  }, [rows]);

  // Counts ROWS the panel can render — series-and-standalone, never occurrences
  // — so "Showing N of M" and the header pill stay true of what is on screen.
  // Counting `rows.length` here would put a card reading 52 above a list of 1.
  const total = upcoming.length + past.length;
  // Expanded on open exactly when there is nothing upcoming — on a closed loan
  // or a stale lead the whole history is behind the toggle, and the tab must
  // not open looking empty.
  const showPast = showPastOverride ?? upcoming.length === 0;
  // The budget is spent on upcoming first, so a record with 60 future meetings
  // starts with no past rows — and the "Show 50 more" control below is how its
  // history is still reachable.
  const pastLimit = Math.max(0, INITIAL_ROWS - upcoming.length) + extraPast;
  const visiblePast = showPast ? past.slice(0, pastLimit) : [];
  const renderedCount = upcoming.length + visiblePast.length;

  // Renders whenever the budget is actually cutting rows off. Clicking also
  // expands the past section: with the budget at zero, revealing an empty
  // section would otherwise be the only visible effect.
  const canShowMore = past.length > pastLimit;
  const isTruncated = renderedCount < total;

  // Null anchor: this list mixes past and upcoming rows across arbitrary dates,
  // so there is no single instant an offset could be true of. The caption names
  // the zone season-neutrally instead.
  const caption = zoneCaption(tz, null);

  const showMore = () => {
    setShowPastOverride(true);
    setExtraPast((n) => n + PAGE_SIZE);
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
            Linked
          </div>
          <h3 className="font-display font-bold text-h3 text-onyx m-0">
            Calendar
            {upcoming.length > 0 && (
              <span
                className="ml-space-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-space-2 rounded-pill bg-gold text-onyx font-display text-[11px] font-bold align-middle"
                aria-label={`${upcoming.length} upcoming ${
                  upcoming.length === 1 ? "appointment" : "appointments"
                }`}
              >
                {upcoming.length}
              </span>
            )}
          </h3>
          {caption && (
            <p className="text-caption text-gray-mid m-0 mt-space-1">{caption}</p>
          )}
        </div>
        {!entityArchived && (
          <Button size="sm" type="button" onClick={() => setScheduleOpen(true)}>
            <Plus size={14} aria-hidden /> Schedule appointment
          </Button>
        )}
      </div>

      {q.isLoading && (
        <ul
          className="flex flex-col gap-space-2 m-0 p-0 list-none"
          aria-label="Loading appointments"
        >
          {[0, 1, 2].map((i) => (
            <li
              key={i}
              className="h-9 rounded-md bg-white-warm animate-pulse"
              aria-hidden
            />
          ))}
        </ul>
      )}

      {q.isError && !q.isLoading && (
        <div
          role="alert"
          className="flex items-center justify-between gap-space-3 p-space-3 rounded-md bg-status-error/10 text-small text-status-error"
        >
          <span>Could not load appointments for this record.</span>
          <Button size="sm" variant="tertiary" type="button" onClick={() => q.refetch()}>
            <RefreshCw size={14} aria-hidden /> Retry
          </Button>
        </div>
      )}

      {q.isSuccess && total === 0 && (
        <div className="text-small text-gray-mid">
          No appointments linked to this record yet.
        </div>
      )}

      {upcoming.length > 0 && (
        <section aria-label="Upcoming appointments">
          <h4 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-2">
            Upcoming
          </h4>
          <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
            {upcoming.map((a) => (
              <AppointmentRow
                key={a.id}
                appointment={a}
                tz={tz}
                onOpen={setEditing}
              />
            ))}
          </ul>
        </section>
      )}

      {past.length > 0 && (
        <section aria-label="Past appointments" className="mt-space-3">
          <div className="flex items-center justify-between mb-space-2">
            <h4 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0">
              Past
            </h4>
            <label className="inline-flex items-center gap-space-2 text-small text-gray-mid cursor-pointer">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => setShowPastOverride(e.target.checked)}
              />
              Show past
            </label>
          </div>
          {showPast && visiblePast.length > 0 && (
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {visiblePast.map((a) => (
                <AppointmentRow
                  key={a.id}
                  appointment={a}
                  tz={tz}
                  onOpen={setEditing}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Never let a capped history look like a complete one. */}
      {isTruncated && (
        <div className="mt-space-3 flex items-center justify-between gap-space-3">
          <span className="text-caption text-gray-mid">
            Showing {renderedCount} of {total}
          </span>
          {canShowMore && (
            <Button size="sm" variant="tertiary" type="button" onClick={showMore}>
              Show {PAGE_SIZE} more
            </Button>
          )}
        </div>
      )}

      {/* Scheduling from here pre-fills this record as the linked record and
          locks it — the whole point of the affordance. */}
      <AppointmentFormModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        prefilledRelatedType={relatedEntityType}
        prefilledRelatedEntityId={relatedEntityId}
        prefilledRelatedEntityLabel={entityLabel}
      />

      {/* A row the viewer can see the title of opens the ordinary edit modal.
          Keyed by id so re-opening a different row re-hydrates the form. */}
      {editing && (
        <AppointmentFormModal
          key={editing.id}
          open
          onClose={() => setEditing(null)}
          appointment={editing}
        />
      )}
    </Card>
  );
}

function AppointmentRow({
  appointment,
  tz,
  onOpen,
}: {
  appointment: AppointmentDto;
  tz: string;
  onOpen: (a: AppointmentDto) => void;
}) {
  // Both branches print a DATE, so both resolve the zone that date is written
  // in. `appointmentDayZone` returns the viewer's zone for anything that is not
  // all-day, so the timed branch is byte-identical — which is the point: the
  // rule is "always resolve", not "resolve when you remember the row might be
  // all-day". The TIMES stay on `tz` unconditionally; a clock time is an instant
  // and belongs where the reader is standing.
  const dayZone = appointmentDayZone(appointment, tz);
  const when = appointment.isAllDay
    ? `${formatDateInTz(appointment.startAt, dayZone)} · All day`
    : `${formatDateInTz(appointment.startAt, dayZone)} · ${formatTimeInTz(
        appointment.startAt,
        tz,
      )}–${formatTimeInTz(appointment.endAt, tz)}`;

  // Redacted: time, busy, and who to talk to. Inert — there is nothing further
  // to reveal, and the owner name is a label, not a link.
  if (appointment.isRedacted) {
    return (
      <li className="flex items-center justify-between gap-space-3 p-space-2 -mx-space-2 text-small">
        <span className="min-w-0 flex-1 truncate text-gray-mid">
          Busy · {appointment.ownerDisplayName}
        </span>
        <span className="shrink-0 text-caption text-gray-mid">{when}</span>
      </li>
    );
  }

  // The server's own sentence — "Repeats every week on Tuesday until Tue 29 Aug
  // 2028". Rendered VERBATIM: recomposing it here would be a second definition
  // of the rule, free to disagree with the one the form and the drawer show.
  // The date on the right of the row is this group's representative occurrence,
  // so the next date is already on screen and is not repeated twice.
  //
  // A redacted row can never reach this: it returns above. That is deliberate
  // and structural rather than a guard here — how often a meeting the viewer is
  // neither owner of nor invited to repeats, and when it stops, is content, and
  // the redaction contract keeps only the time window, status and owner name.
  const repeatNote = appointment.recurrence?.description ?? null;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(appointment)}
        className="w-full flex items-center justify-between gap-space-3 p-space-2 -mx-space-2 rounded-md text-left hover:bg-white-warm"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-small text-onyx">
            {appointment.title}
          </span>
          {repeatNote && (
            <span className="block truncate text-caption text-gray-mid">
              {repeatNote}
            </span>
          )}
        </span>
        <span className="shrink-0 text-caption text-gray-mid">{when}</span>
      </button>
    </li>
  );
}
